import React from 'react';
import { Users, Clock, TrendingUp, AlertCircle } from 'lucide-react';

const StaffOptimization: React.FC = () => {
  const currentStaff = [
    { name: 'Sarah M.', role: 'Sales Associate', zone: 'Women\'s Section', performance: 92 },
    { name: 'James K.', role: 'Sales Associate', zone: 'Men\'s Section', performance: 88 },
    { name: 'Lisa P.', role: 'Cashier', zone: 'Checkout', performance: 95 },
    { name: 'Marcus D.', role: 'Stock Associate', zone: 'Stockroom', performance: 85 }
  ];

  const hourlyNeeds = [
    { hour: '9 AM', required: 4, scheduled: 4, status: 'optimal' },
    { hour: '10 AM', required: 5, scheduled: 4, status: 'understaffed' },
    { hour: '11 AM', required: 6, scheduled: 6, status: 'optimal' },
    { hour: '12 PM', required: 8, scheduled: 7, status: 'understaffed' },
    { hour: '1 PM', required: 8, scheduled: 8, status: 'optimal' },
    { hour: '2 PM', required: 7, scheduled: 8, status: 'overstaffed' },
    { hour: '3 PM', required: 6, scheduled: 6, status: 'optimal' }
  ];

  const metrics = [
    { label: 'Staff on Duty', value: '12', change: '+2', icon: Users },
    { label: 'Avg. Customer / Staff', value: '10.6', change: '-5%', icon: Users },
    { label: 'Response Time', value: '2.3m', change: '-18%', icon: Clock },
    { label: 'Staff Utilization', value: '87%', change: '+12%', icon: TrendingUp }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'optimal': return 'var(--sca-teal)';
      case 'understaffed': return '#ef4444';
      case 'overstaffed': return '#f59e0b';
      default: return 'var(--sca-muted)';
    }
  };

  return (
    <div className="p-6" style={{ maxWidth: '1400px', margin: '0 auto' }}>
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Staff Optimization
        </h1>
        <p style={{ color: 'var(--sca-muted)' }}>
          Optimize staffing levels based on traffic patterns and performance
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {metrics.map((metric, idx) => (
          <div key={idx} className="card">
            <div className="flex items-center justify-between mb-3">
              <metric.icon size={24} style={{ color: 'var(--sca-teal)' }} />
              <span className="text-xs px-2 py-1 rounded-full" style={{
                backgroundColor: 'var(--sca-badge-bg)',
                color: 'var(--sca-teal)'
              }}>
                {metric.change}
              </span>
            </div>
            <div className="text-2xl font-bold mb-1" style={{ color: 'var(--sca-ink)' }}>
              {metric.value}
            </div>
            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              {metric.label}
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Current Staff
          </h3>
          <div className="space-y-3">
            {currentStaff.map((staff, idx) => (
              <div
                key={idx}
                className="p-3 rounded-lg"
                style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
                      {staff.name}
                    </div>
                    <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                      {staff.role} • {staff.zone}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold" style={{ color: 'var(--sca-teal)' }}>
                      {staff.performance}%
                    </div>
                    <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                      Performance
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
            Staffing Needs
          </h3>
          <div className="space-y-3">
            {hourlyNeeds.map((hour, idx) => (
              <div key={idx}>
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium" style={{ color: 'var(--sca-ink)' }}>
                      {hour.hour}
                    </span>
                    {hour.status !== 'optimal' && (
                      <AlertCircle size={14} style={{ color: getStatusColor(hour.status) }} />
                    )}
                  </div>
                  <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                    {hour.scheduled} / {hour.required} staff
                  </span>
                </div>
                <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'var(--sca-border)' }}>
                  <div
                    className="h-2 rounded-full transition-all"
                    style={{
                      width: `${(hour.scheduled / hour.required) * 100}%`,
                      backgroundColor: getStatusColor(hour.status)
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card">
        <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
          Optimization Recommendations
        </h3>
        <div className="space-y-3">
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}>
            <div className="font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
              Add staff during lunch rush
            </div>
            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              Schedule 1 additional sales associate for 12-1 PM to handle peak traffic
            </div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--sca-hover)', border: '1px solid var(--sca-border)' }}>
            <div className="font-semibold mb-1" style={{ color: 'var(--sca-ink)' }}>
              Optimize break schedules
            </div>
            <div className="text-sm" style={{ color: 'var(--sca-muted)' }}>
              Stagger breaks to maintain coverage during mid-morning traffic spike
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffOptimization;
